import React, { useState, useEffect, useMemo, useCallback } from 'react';

// Icon Imports
import AnnounceIcon from '../components/icons/AnnounceIcon';
import BellIcon from '../components/icons/BellIcon';
import DashboardIcon from '../components/icons/DashboardIcon';
import FeedbackIcon from '../components/icons/FeedbackIcon';
import FeeIcon from '../components/icons/FeeIcon';
import HistoryIcon from '../components/icons/HistoryIcon';
import InstallmentIcon from '../components/icons/InstallmentIcon';
import PaymentIcon from '../components/icons/PaymentIcon';
import ProfileIcon from '../components/icons/ProfileIcon';
import ReceiptsIcon from '../components/icons/ReceiptsIcon';
import ReportsIcon from '../components/icons/ReportsIcon';
import ScholarshipIcon from '../components/icons/ScholarshipIcon';
import SettingsIcon from '../components/icons/SettingsIcon';
import UsersIcon from '../components/icons/UsersIcon';
import VerifyIcon from '../components/icons/VerifyIcon';
import GraduationCapIcon from '../components/icons/GraduationCapIcon';
import DocumentCollectionIcon from '../components/icons/DocumentCollectionIcon';
import CalendarIcon from '../components/icons/CalendarIcon';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { SidebarLink } from '../types';

import { useAuth } from './useAuth';
import { useInjectedService } from './useInjectedService';
import logger from '../services/loggerService';
import { ADMIN_FALLBACK, STUDENT_FALLBACK, FallbackNavLink, ROLE_SIGNATURES, NAV_CACHE_CONFIG, UNIVERSAL_FALLBACK, SHELL_PREFIX_MAP } from '../config/navigationConfig';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import useWebSocket from './useWebSocket';
import ConfigurationService from '../services/ConfigurationService';

/**
 * Icon map to convert string icon names from the API to actual React components.
 * This enables 100% dynamic configuration - new icons can be added here as needed.
 */
const iconMap: Record<string, React.ReactElement> = {
  DashboardIcon: React.createElement(DashboardIcon, { className: 'w-4 h-4' }),
  UsersIcon: React.createElement(UsersIcon, { className: 'w-4 h-4' }),
  ScholarshipIcon: React.createElement(ScholarshipIcon, { className: 'w-4 h-4' }),
  FeedbackIcon: React.createElement(FeedbackIcon, { className: 'w-4 h-4' }),
  PaymentIcon: React.createElement(PaymentIcon, { className: 'w-4 h-4' }),
  SettingsIcon: React.createElement(SettingsIcon, { className: 'w-4 h-4' }),
  ReportsIcon: React.createElement(ReportsIcon, { className: 'w-4 h-4' }),
  ProfileIcon: React.createElement(ProfileIcon, { className: 'w-4 h-4' }),
  VerifyIcon: React.createElement(VerifyIcon, { className: 'w-4 h-4' }),
  FeeIcon: React.createElement(FeeIcon, { className: 'w-4 h-4' }),
  InstallmentIcon: React.createElement(InstallmentIcon, { className: 'w-4 h-4' }),
  AnnounceIcon: React.createElement(AnnounceIcon, { className: 'w-4 h-4' }),
  BellIcon: React.createElement(BellIcon, { className: 'w-4 h-4' }),
  HistoryIcon: React.createElement(HistoryIcon, { className: 'w-4 h-4' }),
  ReceiptsIcon: React.createElement(ReceiptsIcon, { className: 'w-4 h-4' }),
  GraduationCapIcon: React.createElement(GraduationCapIcon, { className: 'w-4 h-4' }),
  DocumentCollectionIcon: React.createElement(DocumentCollectionIcon, { className: 'w-4 h-4' }),
  CalendarIcon: React.createElement(CalendarIcon, { className: 'w-4 h-4' }),
  ShieldCheckIcon: React.createElement(ShieldCheckIcon, { className: 'w-4 h-4' }),
};


/**
 * Interface for the raw navigation link from API
 */
interface ApiNavLink {
  name: string;
  path?: string;
  icon: string;
  subLinks?: ApiNavLink[];
}

/**
 * Transforms API response links to include React component icons recursively.
 */
const transformApiLinks = (apiLinks: ApiNavLink[]): SidebarLink[] => {
  return apiLinks.map((link) => {
    const iconName = link.icon;
    const resolvedIcon = iconMap[iconName as keyof typeof iconMap];
    if (iconName && !resolvedIcon && iconName !== 'DashboardIcon') {
      logger.warn(`[Navigation] Icon "${iconName}" not found in frontend iconMap registry. Falling back to DashboardIcon.`, { iconName, linkName: link.name });
    }
    return {
      ...link,
      icon: resolvedIcon || React.createElement(DashboardIcon, { className: 'w-4 h-4' }),
      subLinks: link.subLinks
        ? transformApiLinks(link.subLinks)
        : undefined,
    };
  });
};

/**
 * Recursively transforms FallbackNavLink[] to SidebarLink[] with React icon components.
 */
const transformFallbackLinks = (links: FallbackNavLink[]): SidebarLink[] => {
  return links.map((link) => {
    const iconName = link.icon;
    const resolvedIcon = iconMap[iconName];
    if (iconName && !resolvedIcon && iconName !== 'DashboardIcon') {
      logger.warn(`[Navigation] Fallback Icon "${iconName}" not found in frontend iconMap registry. Falling back to DashboardIcon.`, { iconName, linkName: link.name });
    }
    return {
      name: link.name,
      path: link.path,
      icon: resolvedIcon || React.createElement(DashboardIcon),
      subLinks: link.subLinks ? transformFallbackLinks(link.subLinks) : undefined,
    };
  });
};

/**
 * Gets the appropriate fallback navigation based on user's role.
 * @param userRole - The user's role key from the backend
 * @returns Navigation links appropriate for the user's role
 */
/**
 * Gets the appropriate fallback navigation based on user's role and default path.
 * @param defaultPath - The user's default dashboard path (e.g. '/admin/dashboard')
 * @param userRole - The user's role key from the backend
 * @returns Navigation links appropriate for the user's role
 */
const getFallbackNavigation = (defaultPath: string | undefined, userRole: string): SidebarLink[] => {
  const path = defaultPath || '';
  const matched = SHELL_PREFIX_MAP.find((entry) => path.startsWith(entry.prefix));

  if (matched) {
    return transformFallbackLinks(matched.fallback);
  }

  // Dynamic Role Fallback: search key signatures if defaultPath is absent or custom
  const roleKey = (userRole || '').toUpperCase();
  if (roleKey.includes(ROLE_SIGNATURES.ADMIN)) {
    return transformFallbackLinks(ADMIN_FALLBACK);
  } else if (roleKey.includes(ROLE_SIGNATURES.STUDENT)) {
    return transformFallbackLinks(STUDENT_FALLBACK);
  }

  // Universal Fallback if no matching prefix/signature was found
  return transformFallbackLinks(UNIVERSAL_FALLBACK);
};

interface UseAppNavigationReturn {
  links: SidebarLink[];
  isLoading: boolean;
}

/**
 * Enterprise-grade navigation hook with optimized performance.
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * 1. Instant Render: Shows fallback navigation immediately (no loading delay)
 * 2. Universal Caching: Caches API response using IStorageService
 * 3. Background Fetch: Fetches API data in background without blocking UI
 */
export const useAppNavigation = (): UseAppNavigationReturn => {
  const { user } = useAuth();
  const storage = useInjectedService('storage');
  const network = useInjectedService('network');

  // Compute fallback links immediately (no async needed)
  const fallbackLinks = useMemo(() => {
    if (!user?.role) return [];
    return getFallbackNavigation(user.defaultPath, user.role);
  }, [user?.role, user?.defaultPath]);

  // Start with fallback links immediately (INSTANT RENDER)
  const [links, setLinks] = useState<SidebarLink[]>(fallbackLinks);
  const isLoading = false;
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Initialize WebSocket for real-time invalidation
  const isAuthenticated = user !== null;
  const socketUrl = useMemo(() => {
    try {
      return ConfigurationService.getSocketUrl();
    } catch {
      return '';
    }
  }, []);

  const { on: onWs, off: offWs } = useWebSocket(socketUrl, isAuthenticated);

  // Get cache key for current user (user-scoped to prevent data leakage between users)
  const getCacheKey = useCallback(() => {
    return user?.id && user?.role ? `${NAV_CACHE_CONFIG.KEY_PREFIX}${user.role}-${user.id}` : null;
  }, [user?.role, user?.id]);

  // WebSocket invalidation listener
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handleInvalidation = (data: unknown) => {
      logger.info('Navigation cache invalidation event received via WebSocket', { data, userId: user.id });
      const cacheKey = getCacheKey();
      if (cacheKey) {
        storage.remove(cacheKey)
          .then(() => {
            logger.debug('Navigation cache successfully invalidated', { cacheKey });
            setRefreshTrigger((prev) => prev + 1);
          })
          .catch((err: Error) => {
            logger.warn('Failed to invalidate navigation cache', { error: err.message });
          });
      } else {
        setRefreshTrigger((prev) => prev + 1);
      }
    };

    const wsEvent = ConfigurationService.getWsEventRolePermissionsUpdated();
    onWs(wsEvent, handleInvalidation);
    return () => {
      offWs(wsEvent, handleInvalidation);
    };
  }, [onWs, offWs, getCacheKey, storage, isAuthenticated, user]);

  useEffect(() => {
    if (!user) {
      setLinks([]);
      return;
    }

    // Set fallback links immediately for instant render
    setLinks(fallbackLinks);

    const fetchNavigation = async () => {
      const cacheKey = getCacheKey();
      let cachedEtag: string | undefined;

      // 1. Try to load from universal cache first
      if (cacheKey) {
        try {
          const cached = await storage.get<{ timestamp: number; initialTimestamp?: number; etag?: string; data: ApiNavLink[] }>(cacheKey);
          if (cached && typeof cached === 'object' && cached.timestamp && Array.isArray(cached.data)) {
            cachedEtag = cached.etag;
            const ageSeconds = (Date.now() - cached.timestamp) / 1000;
            const initialTimestamp = cached.initialTimestamp || cached.timestamp;
            const totalAgeSeconds = (Date.now() - initialTimestamp) / 1000;

            if (ageSeconds < ConfigurationService.getNavigationCacheTtlSeconds() && totalAgeSeconds < ConfigurationService.getNavigationMaxCacheAgeSeconds()) { // Sliding-window TTL + Hard Ceiling (database-driven)
              const cachedLinks = transformApiLinks(cached.data);
              if (cachedLinks.length > 0) {
                setLinks(cachedLinks);
                logger.debug('Navigation loaded from cache', { role: user.role, count: cachedLinks.length, ageSeconds, totalAgeSeconds, etag: cachedEtag });
              }
            } else {
              logger.debug('Navigation cache expired or reached hard ceiling', { role: user.role, ageSeconds, totalAgeSeconds });
            }
          }
        } catch (err: unknown) {
          logger.warn('Failed to read navigation cache', { error: (err as Error).message });
        }
      }

      // 2. Fetch from API in background (stale-while-revalidate pattern)
      try {
        const response = await network.request<ApiNavLink[]>({
          url: API_ENDPOINTS.UI.NAVIGATION,
          method: 'GET',
          headers: cachedEtag ? { 'If-None-Match': cachedEtag } : undefined,
        });

        // Handle 304 Not Modified
        if (response.status === 304) {
          logger.debug('Navigation not modified (304), keeping cached version', { role: user.role, etag: cachedEtag });
          // Slide the sliding-window TTL forward in cache (respecting the existing initialTimestamp)
          if (cacheKey && cachedEtag) {
            try {
              const cached = await storage.get<{ timestamp: number; initialTimestamp?: number; etag?: string; data: ApiNavLink[] }>(cacheKey);
              if (cached) {
                const initialTimestamp = cached.initialTimestamp || cached.timestamp;
                await storage.save(cacheKey, {
                  ...cached,
                  initialTimestamp,
                  timestamp: Date.now(),
                });
              }
            } catch (e) {
              // Ignore
            }
          }
          return;
        }

        const apiLinks = response.data;
        const responseEtag = response.headers['etag'] || response.headers['ETag'];

        if (apiLinks && apiLinks.length > 0) {
          // If the etag matches what we currently have in cache, skip state update
          if (cachedEtag && responseEtag && cachedEtag === responseEtag) {
            logger.debug('Navigation matches cached ETag, skipping state update', { role: user.role, etag: responseEtag });
            // Slide window forward
            try {
              const cached = await storage.get<{ timestamp: number; initialTimestamp?: number; etag?: string; data: ApiNavLink[] }>(cacheKey!);
              const initialTimestamp = (cached && cached.initialTimestamp) || (cached && cached.timestamp) || Date.now();
              const cachePayload = {
                timestamp: Date.now(),
                initialTimestamp,
                etag: responseEtag,
                data: apiLinks,
              };
              await storage.save(cacheKey!, cachePayload);
            } catch (e) {
              // Ignore
            }
            return;
          }

          const transformedLinks = transformApiLinks(apiLinks);

          // Save to shared cache for reuse with explicit sliding-window TTL and ETag
          if (cacheKey) {
            try {
              const cached = await storage.get<{ timestamp: number; initialTimestamp?: number; etag?: string; data: ApiNavLink[] }>(cacheKey);
              const initialTimestamp = (cached && cached.initialTimestamp) || Date.now();
              const cachePayload = {
                timestamp: Date.now(),
                initialTimestamp,
                etag: responseEtag,
                data: apiLinks,
              };
              await storage.save(cacheKey, cachePayload);
            } catch (err: unknown) {
              logger.warn('Failed to save navigation to cache', { error: (err as Error).message });
            }
          }

          setLinks(transformedLinks);
          logger.debug('Navigation refreshed from API', { role: user.role, count: transformedLinks.length, etag: responseEtag });
        }
      } catch (error) {
        logger.warn('Navigation background fetch failed', { error, role: user.role });
      }
    };

    fetchNavigation();
  }, [user, fallbackLinks, getCacheKey, storage, network, refreshTrigger]);

  return { links, isLoading };
};
